import { useCallback, useState } from "react";
import { ChevronDown } from "lucide-react";

import { ErrorState, Money, SkeletonRows, Status, focusRing } from "../../../../components/ui";
import { financeAPI } from "../../../services/businessErpService";
import PayCalculation from "../../hr/payslips/PayCalculation";

// Report 54A (D1): how each employee's pay in a payroll was worked out, as Finance reads it before deciding. Read
// only. The figures are the stored breakdown HR calculated — the same one the employee's payslip will carry — so
// Finance never sees a second version of the arithmetic. Loaded when the reviewer opens it.

const summary = `flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-control py-2 max-md:min-h-touch ${focusRing} [&::-webkit-details-marker]:hidden`;
const chevron = "h-4 w-4 shrink-0 text-tertiary transition-transform group-open:rotate-180";

function Employee({ row }) {
  const { calculation } = row;
  return (
    <li>
      <details className="group/employee border-t border-subtle first:border-t-0">
        <summary className={summary.replace("group-open", "group-open/employee")}>
          <span className="min-w-0">
            <span className="block truncate text-body-sm font-medium text-foreground">{row.name}</span>
            <span className="block text-meta text-secondary">
              {[row.employeeCode, calculation.basic.payBasisLabel].filter(Boolean).join(" · ") || "Pay calculation"}
              {calculation.checks.ok ? null : <Status tone="danger" icon className="ml-2">Does not add up</Status>}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-3">
            <span className="text-right">
              <span className="block text-meta text-secondary">Net pay</span>
              <Money value={calculation.net.netCents} className="text-money text-foreground" />
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-tertiary transition-transform group-open/employee:rotate-180" aria-hidden="true" />
          </span>
        </summary>
        <div className="pb-4 pt-1">
          <PayCalculation calculation={calculation} name={row.name} />
        </div>
      </details>
    </li>
  );
}

export default function PayrollCalculations({ periodId }) {
  const [state, setState] = useState({ status: "idle", payslips: [] });

  const load = useCallback(async () => {
    setState({ status: "loading", payslips: [] });
    try {
      const result = await financeAPI.listPayrollCalculations(periodId);
      const data = result?.data?.payslips ? result.data : result;
      setState({ status: "ready", payslips: data?.payslips || [] });
    } catch {
      setState({ status: "error", payslips: [] });
    }
  }, [periodId]);

  const onToggle = (event) => {
    if (event.currentTarget.open && state.status === "idle") load();
  };

  return (
    <details className="group" onToggle={onToggle}>
      <summary className={summary}>
        <span className="min-w-0">
          <span className="block text-body-sm font-medium text-foreground">How each employee's pay was worked out</span>
          <span className="block text-meta text-secondary">Basic pay, unpaid days, earnings and every deduction, per employee</span>
        </span>
        <ChevronDown className={chevron} aria-hidden="true" />
      </summary>
      <div className="pb-2">
        {state.status === "loading" ? <SkeletonRows rows={3} columns={2} label="Loading the pay calculations" /> : null}
        {state.status === "error" ? (
          <ErrorState title="The pay calculations couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
        ) : null}
        {state.status === "ready" && !state.payslips.length ? <p className="py-2 text-body-sm text-secondary">This payroll has no payslips.</p> : null}
        {state.status === "ready" && state.payslips.length ? (
          <>
            <ul aria-label="Employees in this payroll">
              {state.payslips.map((row) => <Employee key={row.payslipId} row={row} />)}
            </ul>
            <p className="mt-2 text-meta text-tertiary">Payroll works these out. To change one, return the payroll to HR.</p>
          </>
        ) : null}
      </div>
    </details>
  );
}
